import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import '../../scss/partner-page.scss';
import { partners, PartnerData, PartnerOffer } from '../../constants/partner-page.constants';

// ─── Offer Modal ──────────────────────────────────────────────────────────────

const PartnerOfferModal: React.FC<{ partner: PartnerData; offer: PartnerOffer; onClose: () => void }> = ({
    partner,
    offer,
    onClose,
}) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="partner-modal-overlay" onClick={onClose}>
            <div className="partner-modal" onClick={(e) => e.stopPropagation()}>
                <button className="partner-modal__close" onClick={onClose} aria-label="Close">
                    &#x2715;
                </button>

                {/* Header */}
                <div className="partner-modal__header">
                    <div className="partner-modal__logo-wrap">
                        <img src={partner.image} alt={partner.name} className="partner-modal__logo" />
                    </div>
                    <div className="partner-modal__title-block">
                        <h3 className="partner-modal__name">{partner.name}</h3>
                        <span className="partner-modal__subtitle">&mdash; {t(offer.subtitle)}</span>
                    </div>
                </div>

                <p className="partner-modal__intro">{t(offer.intro)}</p>

                {/* Benefits */}
                <div className="partner-modal__section">
                    <h4 className="partner-modal__section-title">{t('PARTNER_OFFER_BENEFITS_TITLE')}</h4>
                    <ul className="partner-modal__benefits-list">
                        {offer.benefits.map((b, i) => (
                            <li key={i} className="partner-modal__benefit-item">
                                <span className="partner-modal__benefit-dot" />
                                {t(b)}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* How To / Steps */}
                {offer.steps && offer.steps.length > 0 && (
                    <div className="partner-modal__section">
                        <h4 className="partner-modal__section-title">{t(offer.howToTitle)}</h4>
                        <ol className="partner-modal__steps-list">
                            {offer.steps.map((step, i) => (
                                <li key={i} className="partner-modal__step-item">{t(step)}</li>
                            ))}
                        </ol>
                        {offer.code && (
                            <div className="partner-modal__code-block">
                                <span className="partner-modal__code">{offer.code}</span>
                                <button
                                    className={`partner-modal__copy-btn${copied ? ' partner-modal__copy-btn--copied' : ''}`}
                                    onClick={() => handleCopy(offer.code!)}
                                    title={copied ? 'Copied!' : 'Copy code'}
                                >
                                    {copied ? (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="5" y="1" width="9" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                            <path d="M3 4H2.5A1.5 1.5 0 0 0 1 5.5v9A1.5 1.5 0 0 0 2.5 16h7A1.5 1.5 0 0 0 11 14.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        )}
                        {offer.noteLines && offer.noteLines.map((line, i) => (
                            <p key={i} className="partner-modal__note">{t(line)}</p>
                        ))}
                    </div>
                )}

                {/* Help / Support */}
                {(offer.helpEmail || offer.helpPhone || offer.helpLink) && (
                    <div className="partner-modal__section">
                        {offer.helpTitle && (
                            <h4 className="partner-modal__section-title">{t(offer.helpTitle)}</h4>
                        )}
                        {offer.helpEmail && (
                            <a href={`mailto:${offer.helpEmail}`} className="partner-modal__contact">
                                {offer.helpEmail}
                            </a>
                        )}
                        {offer.helpPhone && (
                            <a href={`tel:${offer.helpPhone}`} className="partner-modal__contact">
                                {offer.helpPhone}
                            </a>
                        )}
                        {offer.helpLink && (
                            <a
                                href={offer.helpLink.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="partner-modal__contact"
                            >
                                {t(offer.helpLink.label)}
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Partner Card ─────────────────────────────────────────────────────────────

const PartnerCard: React.FC<{ partner: PartnerData; onViewOffer: () => void }> = ({ partner, onViewOffer }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = useState(false);

    return (
        <div className="partner-tile">
            <div
                className="partner-tile__image-wrapper"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <img
                    src={partner.image}
                    alt={partner.name}
                    className="partner-tile__image"
                />
                {partner.offer && (
                    <div className={`partner-tile__overlay${hovered ? ' partner-tile__overlay--visible' : ''}`}>
                        <button
                            className="partner-tile__view-offer-btn"
                            onClick={onViewOffer}
                        >
                            {t('PARTNER_VIEW_OFFER')}
                        </button>
                    </div>
                )}
            </div>
            <div className="partner-tile__body">
                <h5 className="partner-tile__name">{partner.name}</h5>
                <p className="partner-tile__description">{t(partner.description)}</p>
            </div>
        </div>
    );
};

const PartnerPage: React.FC = () => {
    const { t } = useTranslation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeOffer, setActiveOffer] = useState<{ partner: PartnerData } | null>(null);

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId="our-partners"
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}
                <div className="dashboard-main-content">
                    <Header setIsSidebarOpen={setIsSidebarOpen} />
                    <div className="px-3">
                        <div className="partner-page">
                            <div className="partner-page__card">
                                <div className="partner-page__card-header">
                                    <h2 className="partner-page__title">
                                        {t('OUR_PARTNERS')}
                                    </h2>
                                    <p className="partner-page__subtitle">
                                        {t('OUR_PARTNERS_SUBTITLE')}
                                    </p>
                                </div>
                                <div className="partner-page__card-body">
                                    <div className="partner-page__grid">
                                        {partners.map((partner) => (
                                            <PartnerCard
                                                key={partner.id}
                                                partner={partner}
                                                onViewOffer={() => setActiveOffer({ partner })}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {activeOffer && activeOffer.partner.offer && (
                <PartnerOfferModal
                    partner={activeOffer.partner}
                    offer={activeOffer.partner.offer}
                    onClose={() => setActiveOffer(null)}
                />
            )}
        </div>
    );
};

export default PartnerPage;
